/**
 * Submit — primary `Button` bound to RHF form state + an explicit `pending`
 * prop for the network-side mutation (the form is "valid" before the request
 * starts; `pending` is what's true while it's in flight).
 *
 * Disables on `formState.isSubmitting` OR `pending`. Swaps the label to a
 * caller-provided `pendingLabel` while either is true. Trailing arrow icon
 * matches the prototype's primary CTA (`<button>Continue → </button>`).
 */
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { ArrowRight } from "lucide-react";
import { Button, type ButtonProps } from "../primitives/button.js";
import { cn } from "../lib/cn.js";

export interface SubmitProps extends Omit<ButtonProps, "type"> {
  /** True while the mutation is in flight. */
  pending?: boolean;
  /** Label shown while pending or submitting. Default: same children. */
  pendingLabel?: React.ReactNode;
  /** Show the trailing arrow icon. Default true. */
  withArrow?: boolean;
}

export function Submit({
  pending = false,
  pendingLabel,
  disabled,
  withArrow = true,
  children,
  className,
  ...props
}: SubmitProps) {
  const ctx = React.useContext(FormContextProbe);
  const submitting = ctx?.isSubmitting ?? false;
  const isBusy = submitting || pending;

  return (
    <Button
      type="submit"
      disabled={disabled || isBusy}
      className={cn("w-full", className)}
      {...props}
    >
      {isBusy && pendingLabel ? pendingLabel : children}
      {withArrow ? <ArrowRight aria-hidden size={14} strokeWidth={2} /> : null}
    </Button>
  );
}

/**
 * Tiny wrapper context to read `formState.isSubmitting` without forcing every
 * `<Submit>` to be inside a FormProvider. If there's no form context,
 * `useFormContext()` returns null and we fall back to `pending` only.
 */
const FormContextProbe = React.createContext<{ isSubmitting: boolean } | null>(null);

/** Use inside a `<Form>` to surface `formState.isSubmitting` to <Submit/>. */
export function SubmitProbe({ children }: { children: React.ReactNode }) {
  const form = useFormContext();
  const isSubmitting = form?.formState.isSubmitting ?? false;
  const value = React.useMemo(() => ({ isSubmitting }), [isSubmitting]);
  return <FormContextProbe.Provider value={value}>{children}</FormContextProbe.Provider>;
}
